import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import SearchIcon from "@material-ui/icons/Search";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import useOsCatalogs from "../../hooks/useOsCatalogs";
import { AuthContext } from "../../context/Auth/AuthContext";

const N2_GROUP_ID = 5;

const useStyles = makeStyles(theme => ({
  form: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden"
  },
  content: {
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5)
  },
  relationRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1)
  },
  relationPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap"
  },
  files: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  dropzone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  readOnly: {
    backgroundColor: theme.palette.action.hover
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1)
  }
}));

const responseItems = response => response?.data?.data || [];

const formatClient = client => {
  if (!client) return "";
  const primaryName = client.razao || client.nome || client.fantasia || `Cliente ${client.id}`;
  const fantasy =
    client.fantasia &&
    client.fantasia.trim().toLocaleLowerCase() !== primaryName.trim().toLocaleLowerCase()
      ? ` · ${client.fantasia}`
      : "";
  const document = client.cnpj_cpf ? ` - ${client.cnpj_cpf}` : "";

  return `${primaryName}${fantasy}${document}`;
};

const OsCreateForm = ({ parentOrder = null, onCancel, onCreated }) => {
  const classes = useStyles();
  const { data: sharedCatalogs } = useOsCatalogs();
  const { user: currentUser } = React.useContext(AuthContext);
  const currentTechnicianId = currentUser?.IDTecnicoOS;
  const fixedGroup = Boolean(parentOrder);
  const [catalogs, setCatalogs] = useState({
    grupos: [],
    prioridades: [],
    tipos: [],
    canais: [],
    tecnicos: [],
    clientes: []
  });
  const [groupId, setGroupId] = useState(parentOrder ? N2_GROUP_ID : 1);
  const [client, setClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [objects, setObjects] = useState([]);
  const [objectId, setObjectId] = useState("");
  const [defaultObjects, setDefaultObjects] = useState([]);
  const [defaultObjectId, setDefaultObjectId] = useState("");
  const [technicianId, setTechnicianId] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [channelId, setChannelId] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [charge, setCharge] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [files, setFiles] = useState([]);
  const [relationId, setRelationId] = useState("");
  const [relationType, setRelationType] = useState("pai");
  const [relations, setRelations] = useState([]);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingRelation, setLoadingRelation] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sharedCatalogs) return;
    setCatalogs(previous => ({ ...previous, ...sharedCatalogs }));
  }, [sharedCatalogs]);

  useEffect(() => {
    let active = true;
    if (!groupId) return undefined;
    setLoadingObjects(true);
    setObjectId("");
    setDefaultObjectId("");
    api
      .get("/os/objetos", { params: { grupo_id: groupId, per_page: 200 } })
      .then(response => {
        if (active) setObjects(responseItems(response));
      })
      .catch(error => active && toastError(error))
      .finally(() => active && setLoadingObjects(false));
    return () => {
      active = false;
    };
  }, [groupId]);

  useEffect(() => {
    let active = true;
    if (!objectId) {
      setDefaultObjects([]);
      setDefaultObjectId("");
      return undefined;
    }
    setLoadingDefaults(true);
    api
      .get("/os/objetos-padrao", {
        params: { objeto_id: objectId, per_page: 200 }
      })
      .then(response => {
        if (active) setDefaultObjects(responseItems(response));
      })
      .catch(error => active && toastError(error))
      .finally(() => active && setLoadingDefaults(false));
    return () => {
      active = false;
    };
  }, [objectId]);

  useEffect(() => {
    if (!parentOrder?.cliente_id) return;
    const loadParentClient = async () => {
      try {
        const response = await api.get(`/os/clientes/${parentOrder.cliente_id}`);
        const loadedClient = response.data?.data || response.data;
        setClient(loadedClient);
        setCatalogs(previous => ({
          ...previous,
          clientes: [loadedClient, ...previous.clientes]
            .filter(Boolean)
            .filter((item, index, all) => all.findIndex(value => value.id === item.id) === index)
        }));
      } catch (error) {
        toastError(error);
      }
    };
    loadParentClient();
    setContactId(parentOrder.contato_id || "");
    setShortDescription(parentOrder.descricao_curta || "");
    setDescription(parentOrder.descricao || "");
    setPriorityId(parentOrder.prioridade_id || "");
    setChannelId(parentOrder.canal_id || "");
    setTechnicianId(parentOrder.tecnico_id || "");
    setRelations([
      {
        ordem_relacionada_id: parentOrder.id,
        tipo: "pai",
        label: `OS #${parentOrder.id} - ${parentOrder.descricao_curta || "Pai"}`
      }
    ]);
  }, [parentOrder]);

  useEffect(() => {
    if (!client?.id) {
      setContacts([]);
      if (!parentOrder) setContactId("");
      return;
    }
    api
      .get(`/os/terceiros/${client.id}/contatos`, { params: { per_page: 200 } })
      .then(response => setContacts(responseItems(response)))
      .catch(toastError);
  }, [client, parentOrder]);

  useEffect(() => {
    if (!clientSearch || clientSearch.trim().length < 2 || fixedGroup) return undefined;
    let active = true;
    const timeout = setTimeout(() => {
      setLoadingClients(true);
      api
        .get("/os/clientes", { params: { q: clientSearch.trim(), per_page: 20 } })
        .then(response => {
          if (active) {
            setCatalogs(previous => ({
              ...previous,
              clientes: responseItems(response)
            }));
          }
        })
        .catch(error => active && toastError(error))
        .finally(() => active && setLoadingClients(false));
    }, 350);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [clientSearch, fixedGroup]);

  useEffect(() => {
    if (!sharedCatalogs || !groupId) return;
    const types = sharedCatalogs.tipos.filter(
      item => String(item.grupo_id) === String(groupId)
    );
    if (!typeId && types.length === 1) setTypeId(types[0].id);
    if (!priorityId && sharedCatalogs.prioridades.length) {
      const classified = sharedCatalogs.prioridades.find(item =>
        String(item.nome).toLowerCase().includes("class")
      );
      setPriorityId((classified || sharedCatalogs.prioridades[0]).id);
    }
    if (!channelId && sharedCatalogs.canais.length) {
      setChannelId(sharedCatalogs.canais[0].id);
    }
    if (!technicianId && currentTechnicianId) {
      setTechnicianId(currentTechnicianId);
    }
  }, [sharedCatalogs, groupId, typeId, priorityId, channelId, technicianId, currentTechnicianId]);

  const groupTypes = useMemo(
    () =>
      catalogs.tipos.filter(
        item => String(item.grupo_id) === String(groupId)
      ),
    [catalogs.tipos, groupId]
  );

  const contactOptions = useMemo(
    () =>
      contacts.map(contact => ({
        ...contact,
        id: contact.id,
        nome: contact.contato || contact.nome || `Contato ${contact.id}`
      })),
    [contacts]
  );

  const addRelation = async () => {
    const id = Number(relationId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.warning("Informe um número de OS válido.");
      return;
    }
    if (relations.some(item => Number(item.ordem_relacionada_id) === id)) {
      toast.warning("Essa OS já foi adicionada.");
      return;
    }
    setLoadingRelation(true);
    try {
      const response = await api.get(`/os/ordens-servico/${id}`);
      const order = response.data?.data || response.data;
      setRelations(previous => [
        ...previous,
        {
          ordem_relacionada_id: id,
          tipo: relationType,
          label: `OS #${id} - ${order.descricao_curta || ""}`
        }
      ]);
      setRelationId("");
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingRelation(false);
    }
  };

  const removeRelation = id => {
    if (parentOrder && Number(id) === Number(parentOrder.id)) return;
    setRelations(previous =>
      previous.filter(item => Number(item.ordem_relacionada_id) !== Number(id))
    );
  };

  const handleFiles = event => {
    const selected = Array.from(event.target.files || []);
    setFiles(previous => [...previous, ...selected].slice(0, 10));
    event.target.value = "";
  };

  const submit = async event => {
    event.preventDefault();
    if (!groupId || !client?.id || !objectId || !priorityId || !typeId || !channelId || !(technicianId || currentTechnicianId)) {
      toast.error("Preencha grupo, cliente, objeto, prioridade, tipo, canal e técnico.");
      return;
    }
    if (!shortDescription.trim() || !description.trim()) {
      toast.error("Preencha a descrição curta e a descrição.");
      return;
    }

    const form = new FormData();
    const values = {
      grupo_id: groupId,
      cliente_id: client.id,
      ...(contactId ? { contato_id: contactId } : {}),
      tecnico_id: technicianId || currentTechnicianId,
      objeto_id: objectId,
      ...(defaultObjectId ? { objeto_padrao_id: defaultObjectId } : {}),
      prioridade_id: priorityId,
      tipo_id: typeId,
      canal_id: channelId,
      descricao_curta: shortDescription.trim(),
      descricao: description.trim(),
      ...(startDate ? { data_previsao_inicio: startDate } : {}),
      ...(endDate ? { data_previsao_termino: endDate } : {}),
      cobrar: charge,
      valor_total: totalValue || 0,
      tempo_estimado: estimatedTime || 0,
      relacionamentos: relations
        .filter(item => !parentOrder || Number(item.ordem_relacionada_id) !== Number(parentOrder.id) || item.tipo === "pai")
        .map(({ ordem_relacionada_id, tipo }) => ({ ordem_relacionada_id, tipo }))
    };

    Object.entries(values).forEach(([name, value]) => {
      if (value === undefined || value === null || value === "") return;
      form.append(name, name === "relacionamentos" ? JSON.stringify(value) : String(value));
    });
    files.forEach(file => form.append("anexos", file));

    setSaving(true);
    try {
      const response = await api.post("/os/ordens-servico", form);
      const created = response.data?.data || response.data;
      toast.success(`OS #${created?.id || ""} criada com sucesso.`);
      onCreated(created);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const selectField = (label, value, onChange, options, disabled = false) => (
    <FormControl variant="outlined" size="small" fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={onChange} label={label}>
        {options.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.nome}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <form className={classes.form} onSubmit={submit}>
      <div className={classes.content}>
        {parentOrder && (
          <Paper className={classes.section} variant="outlined">
            <Typography className={classes.sectionTitle}>OS pai</Typography>
            <Typography>
              <strong>OS #{parentOrder.id}</strong> — {parentOrder.descricao_curta || "Sem descrição"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              A nova ordem será criada no grupo Tarefas (N2) e ficará vinculada automaticamente.
            </Typography>
          </Paper>
        )}

        <Paper className={classes.section} variant="outlined">
          <Typography className={classes.sectionTitle}>Terceiro</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Autocomplete
                options={catalogs.clientes}
                value={client}
                loading={loadingClients}
                disabled={fixedGroup}
                getOptionLabel={formatClient}
                getOptionSelected={(option, value) => option.id === value.id}
                filterOptions={options => options}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setClientSearch(value);
                  }
                }}
                onChange={(_, value) => {
                  setClient(value);
                  setContactId("");
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Cliente"
                    variant="outlined"
                    required
                    helperText="Busque por razão social, fantasia ou CNPJ/CPF"
                    className={fixedGroup ? classes.readOnly : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingClients ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              {selectField(
                "Contato",
                contactId,
                event => setContactId(event.target.value),
                [{ id: "", nome: "Sem contato" }, ...contactOptions],
                !client
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.section} variant="outlined">
          <Typography className={classes.sectionTitle}>Objeto e classificação</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              {selectField(
                "Grupo",
                groupId,
                event => setGroupId(event.target.value),
                catalogs.grupos,
                fixedGroup
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {selectField(
                loadingObjects ? "Carregando objetos..." : "Objeto",
                objectId,
                event => setObjectId(event.target.value),
                objects,
                !groupId || loadingObjects
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {selectField(
                loadingDefaults ? "Carregando objetos padrão..." : "Objeto padrão",
                defaultObjectId,
                event => setDefaultObjectId(event.target.value),
                [{ id: "", nome: "Sem objeto padrão" }, ...defaultObjects],
                !objectId || loadingDefaults
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectField("Tipo", typeId, event => setTypeId(event.target.value), groupTypes)}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectField(
                "Prioridade",
                priorityId,
                event => setPriorityId(event.target.value),
                catalogs.prioridades
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectField(
                "Canal",
                channelId,
                event => setChannelId(event.target.value),
                catalogs.canais
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              {selectField(
                "Técnico",
                technicianId,
                event => setTechnicianId(event.target.value),
                catalogs.tecnicos
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.section} variant="outlined">
          <Typography className={classes.sectionTitle}>Planejamento</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Previsão de início"
                type="datetime-local"
                value={startDate}
                onChange={event => setStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Previsão de término"
                type="datetime-local"
                value={endDate}
                onChange={event => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Tempo estimado (minutos)"
                type="number"
                value={estimatedTime}
                onChange={event => setEstimatedTime(event.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Valor total (R$)"
                type="number"
                value={totalValue}
                onChange={event => setTotalValue(event.target.value)}
                disabled={!charge}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant={charge ? "contained" : "outlined"}
                color="primary"
                onClick={() => setCharge(value => !value)}
              >
                {charge ? "Cobrar esta OS" : "Não cobrar esta OS"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.section} variant="outlined">
          <Typography className={classes.sectionTitle}>Detalhes</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                variant="outlined"
                label="Descrição curta"
                value={shortDescription}
                onChange={event => setShortDescription(event.target.value)}
                inputProps={{ maxLength: 254 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={8}
                variant="outlined"
                label="Descrição"
                value={description}
                onChange={event => setDescription(event.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.section} variant="outlined">
          <Typography className={classes.sectionTitle}>Anexos</Typography>
          <div className={classes.dropzone}>
            <Typography variant="body2" color="textSecondary">
              Adicione até 10 arquivos de 20 MB cada.
            </Typography>
            <Button component="label" startIcon={<AttachFileIcon />} variant="outlined">
              Adicionar anexos
              <input type="file" hidden multiple onChange={handleFiles} />
            </Button>
          </div>
          <div className={classes.files}>
            {files.map((file, index) => (
              <Chip
                key={`${file.name}-${index}`}
                icon={<AttachFileIcon />}
                label={file.name}
                onDelete={() => setFiles(previous => previous.filter((_, itemIndex) => itemIndex !== index))}
              />
            ))}
          </div>
        </Paper>

        <Paper className={classes.section} variant="outlined">
          <Typography className={classes.sectionTitle}>OS relacionadas</Typography>
          <div className={classes.relationRow}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Número da OS"
              type="number"
              value={relationId}
              onChange={event => {
                setRelationId(event.target.value);
              }}
              onKeyDown={event => event.key === "Enter" && (event.preventDefault(), addRelation())}
              InputProps={{ startAdornment: <SearchIcon color="action" /> }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Relação</InputLabel>
              <Select value={relationType} onChange={event => setRelationType(event.target.value)} label="Relação">
                <MenuItem value="pai">OS relacionada é pai</MenuItem>
                <MenuItem value="filha">OS relacionada é filha</MenuItem>
              </Select>
            </FormControl>
            <IconButton color="primary" onClick={addRelation} disabled={loadingRelation}>
              {loadingRelation ? <CircularProgress size={22} /> : <AddIcon />}
            </IconButton>
          </div>
          <div className={classes.relationPreview}>
            {relations.map(relation => (
              <Chip
                key={`${relation.ordem_relacionada_id}-${relation.tipo}`}
                label={`${relation.label} (${relation.tipo === "pai" ? "Pai" : "Filha"})`}
                onDelete={parentOrder && Number(relation.ordem_relacionada_id) === Number(parentOrder.id)
                  ? undefined
                  : () => removeRelation(relation.ordem_relacionada_id)}
                color={relation.tipo === "pai" ? "primary" : "default"}
              />
            ))}
          </div>
        </Paper>

        <div className={classes.footer}>
          <Button onClick={onCancel} disabled={saving}>Cancelar</Button>
          <ButtonWithSpinner
            type="submit"
            color="primary"
            variant="contained"
            loading={saving}
            disabled={saving}
          >
            Criar OS
          </ButtonWithSpinner>
        </div>
      </div>
    </form>
  );
};

export default OsCreateForm;
